import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  AppBar,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Toolbar,
  Typography,
} from "@mui/material";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import Box from "@mui/material/Box";
import { useIsFetching } from "@tanstack/react-query";
import { useDeviceDetach } from "hooks/useDeviceDetach";
import { useNavLinks } from "hooks/useNavLinks";
import * as React from "react";
import { CSSProperties, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { CustomOutlet } from "../CustomOutlet/CustomOutlet";

export const BaseLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { navLinks } = useNavLinks();

  const selectedItem = useMemo(() => {
    return navLinks.findIndex((item) =>
      location.pathname.startsWith(`/${item.path.replace(/\?.*$/, "")}`)
    );
  }, [location.pathname, navLinks]);

  const { isMobile } = useDeviceDetach();

  const isFetching = useIsFetching();

  if (isMobile) {
    return (
      <Box sx={styles.mobileLayout}>
        <Box sx={styles.mobileContent}>
          <CustomOutlet />
        </Box>
        <Paper
          sx={{ width: "100%", position: "fixed", bottom: 0 }}
          elevation={3}
        >
          {isFetching ? <LinearProgress /> : null}
          <BottomNavigation
            sx={{ height: "70px" }}
            showLabels
            value={selectedItem}
            onChange={(event, newValue) => {
              navigate(navLinks[newValue].path);
              navLinks[newValue].onClick?.();
            }}
          >
            {navLinks.map((page) => (
              <BottomNavigationAction
                key={page.path}
                label={page.label}
                icon={page.icon ?? <FormatListBulletedIcon />}
              />
            ))}
          </BottomNavigation>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <AppBar position="fixed" sx={styles.appBar} elevation={0}>
        <Container maxWidth="xl">
          <Toolbar>
            <Typography
              variant="h6"
              color="text.secondary"
              component="div"
              sx={{ flexGrow: 1 }}
            >
              Capss
            </Typography>

            <IconButton
              size="large"
              color="default"
              onClick={() => {
                navigate("/settings");
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Toolbar>
        </Container>
        {isFetching ? <LinearProgress /> : null}
        <Divider />
      </AppBar>
      <Toolbar />
      <Container maxWidth="xl">
        <Box sx={styles.navBar}>
          <List component="nav">
            {navLinks.map((page) => (
              <ListItemButton
                key={page.path}
                selected={location.pathname.startsWith(
                  `/${page.path.replace(/\?.*$/, "")}`
                )}
                onClick={() => {
                  navigate(page.path);
                  page.onClick?.();
                }}
              >
                <ListItemIcon>
                  {page.icon ?? <FormatListBulletedIcon />}
                </ListItemIcon>
                <ListItemText primary={page.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
        <Box sx={styles.content}>
          <CustomOutlet />
        </Box>
      </Container>
    </>
  );
};

const styles: { [key: string]: CSSProperties } = {
  appBar: {
    backgroundColor: "background.default",
    zIndex: 10,
  },
  navBar: {
    width: "250px",
    position: "fixed",
  },
  content: {
    flexGrow: 1,
    marginLeft: "260px",
  },
  mobileLayout: {
    width: "100%",
  },
  mobileContent: { width: "100%", marginBottom: "75px" },
};
